import _ from 'lodash';
import { createSelector } from 'reselect';

import { dropDownDocumentFieldTypes } from 'constants/documentFieldTypes';
import createKeyedSelector from 'utils/createKeyedSelector';
import { getOptionDocumentExtracionFieldFromOption } from 'utils/documentExtraction';

// Import the leaf selector from its concrete module rather than the barrel: the
// documents selectors barrel imports back into store/system/selectors, so going
// through it here forms an initialization cycle that leaves this input undefined
// depending on module-load order (surfaces as reselect's "received undefined").
import { getDocumentFilledExtractionFieldIds } from 'store/documents/selectors/document.selectors';

import { getDocumentFieldTypes } from './documentFieldTypes.selectors';
import { getSystem } from './system.selectors';

const selectDocumentExtractionFieldsSlice = createSelector(
  getSystem,
  ({ documentExtractionFields }) => documentExtractionFields,
);

export const getDocumentExtractionFields = createSelector(
  selectDocumentExtractionFieldsSlice,
  slice => _.keyBy(_.sortBy(slice?.fields ?? slice ?? {}, 'orderNumber'), 'id'),
);

/**
 * Returns the raw flat fields array for a given documentTypeId as stored by
 * getInspectorExtractionFieldsByDocTypeIdAction, or null when not yet loaded.
 *
 * The raw flat items have: { id, name, parentId, orderNumber, documentFieldType: { typeDefinition: { type } }, Children: [] }
 * Children will be populated by buildNestedExtractionFields before passing to getExtractionFieldsTree.
 */
// Exported so callers that only know a documentTypeId at combine-time (not selector-definition
// time — e.g. resolving it from a document already read by an earlier input selector) can index
// into it directly, instead of going through the per-id selectInspectorExtractionFieldsByDocTypeId
// factory below (which needs the key up front to build a memoized selector reference).
export const selectInspectorRawFieldsByTypeId = createSelector(
  selectDocumentExtractionFieldsSlice,
  slice => slice?.inspectorFieldsByTypeId ?? {},
);

export const selectInspectorExtractionFieldsByDocTypeId = createKeyedSelector(documentTypeId =>
  createSelector(
    selectInspectorRawFieldsByTypeId,
    inspectorFieldsByTypeId => inspectorFieldsByTypeId[documentTypeId] ?? null,
  ),
);

export const getDocumentExtractionFieldsWithNamedType = createSelector(
  [getDocumentExtractionFields, getDocumentFieldTypes, getDocumentFilledExtractionFieldIds],
  (documentExtractionFields, documentFieldTypes, filledExtractionFieldIds) => {
    if (_.isEmpty(documentFieldTypes)) {
      return [];
    }

    return Object.values(documentExtractionFields).reduce((acc, documentExtractionField) => {
      const { documentFieldTypeId, id } = documentExtractionField;
      const documentFieldType = documentFieldTypes?.[documentFieldTypeId];
      const parentType =
        documentFieldTypes?.[documentFieldType?.parentId]?.typeDefinition?.type || '';
      const type = documentFieldType?.typeDefinition.type || '';

      if (dropDownDocumentFieldTypes.includes(type)) {
        documentFieldType.options.forEach(option => {
          const optionField = getOptionDocumentExtracionFieldFromOption({
            name: option,
            typeParentId: documentFieldType.id,
            parentId: id,
          });

          acc.push(optionField);
        });
      }

      acc.push({
        ...documentExtractionField,
        type,
        parentType,
        isFilled: filledExtractionFieldIds?.includes(id),
      });

      return acc;
    }, []);
  },
);

export const getFieldsTreeGroupedByAiExtractionFieldName = createSelector(
  getDocumentExtractionFieldsWithNamedType,
  documentExtractionFields => _.keyBy(documentExtractionFields, 'aiExtractionFieldName'),
);
